<template>
  <div class="rad-stake-page">
    <RadUserInfoCard
      ref="userInfoCard"
      :activating="activating"
      @activate="onActivate"
    />
    <RadStakingForm
      :parent-address="parentAddress"
      :period-quotas="periodQuotas"
      :min-stake="minStake"
      :submitting-period="submittingPeriod"
      @submit="onSubmit"
    />
    <RadTeamInfo
      :info="teamInfo"
      :withdrawing-type="withdrawingType"
      @withdraw="onWithdraw"
    />
    <RadStakingRecords
      :records="stakingRecords"
      :loading="loading"
      @redeem="onRedeem"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Notify } from "vant";
import RadUserInfoCard from "@/views/radStake/components/RadUserInfoCard.vue";
import RadStakingForm from "@/views/radStake/components/RadStakingForm.vue";
import RadTeamInfo from "@/views/radStake/components/RadTeamInfo.vue";
import RadStakingRecords from "@/views/radStake/components/RadStakingRecords.vue";
import {
  EMPTY_PERIOD_QUOTA,
  EMPTY_RAD_TEAM_INFO,
} from "@/views/radStake/config";
import {
  activateAllCards,
  claimAllRadStakeRewards,
  claimRadCardsReward,
  claimRadPrincipal,
  claimRadShareReward,
  fetchPeriodQuotas,
  fetchRadStakingRecords,
  fetchRadTeamInfo,
  getMinStakeAmount,
  getRadStakeParentAddress,
  resolveRadStakeParentAddress,
  stakeRadToken,
} from "@/contract/services/radStakeService";
import { shouldNotifyRpcError } from "@/contract/index";

export default {
  name: "RadStake",
  components: {
    RadUserInfoCard,
    RadStakingForm,
    RadTeamInfo,
    RadStakingRecords,
  },
  data() {
    return {
      loading: false,
      activating: false,
      submitting: false,
      submittingPeriod: "",
      withdrawingType: "",
      minStake: "",
      parentAddress: "",
      periodQuotas: {
        180: { ...EMPTY_PERIOD_QUOTA },
        360: { ...EMPTY_PERIOD_QUOTA },
        720: { ...EMPTY_PERIOD_QUOTA },
      },
      stakingRecords: [],
      teamInfo: { ...EMPTY_RAD_TEAM_INFO },
    };
  },
  computed: {
    ...mapGetters(["accountAddress"]),
  },
  watch: {
    accountAddress() {
      this.loadPageData();
    },
  },
  mounted() {
    this.loadPageData();
  },
  methods: {
    async loadPageData() {
      this.loading = true;
      try {
        this.minStake = await getMinStakeAmount();
        this.periodQuotas = await fetchPeriodQuotas();

        if (this.accountAddress) {
          const boundParent = await getRadStakeParentAddress(
            this.accountAddress
          );
          this.parentAddress = resolveRadStakeParentAddress(boundParent);
          this.stakingRecords = await fetchRadStakingRecords(
            this.accountAddress
          );
          this.teamInfo = await fetchRadTeamInfo(this.accountAddress);
        } else {
          this.parentAddress = resolveRadStakeParentAddress("");
          this.stakingRecords = [];
          this.teamInfo = { ...EMPTY_RAD_TEAM_INFO };
        }
      } catch (err) {
        if (shouldNotifyRpcError(err)) {
          Notify({
            type: "danger",
            message: err?.message || "加载质押数据失败",
          });
        }
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async onSubmit(data) {
      if (!this.accountAddress) {
        Notify({ type: "warning", message: "请先连接钱包" });
        return;
      }
      if (!data.amount || Number(data.amount) <= 0) {
        Notify({ type: "warning", message: "请输入有效的质押数量" });
        return;
      }
      if (Number(data.amount) < Number(this.minStake)) {
        Notify({
          type: "warning",
          message: `最小质押数量为 ${this.minStake} RAD`,
        });
        return;
      }

      this.submittingPeriod = data.period;
      try {
        await stakeRadToken(data.amount, data.period, this.parentAddress);
        Notify({ type: "success", message: "质押成功" });
        await this.loadPageData();
      } catch (err) {
        //
      } finally {
        this.submittingPeriod = "";
      }
    },
    async onRedeem(record) {
      if (!this.accountAddress) {
        Notify({ type: "warning", message: "请先连接钱包" });
        return;
      }
      if (!record.canRedeem) {
        return;
      }

      this.submitting = true;
      try {
        await claimRadPrincipal(record.index);
        Notify({ type: "success", message: "赎回成功" });
        await this.loadPageData();
      } catch (err) {
        //
      } finally {
        this.submitting = false;
      }
    },
    async onWithdraw(type) {
      if (!this.accountAddress) {
        Notify({ type: "warning", message: "请先连接钱包" });
        return;
      }

      this.withdrawingType = type;
      try {
        if (type === "staking") {
          await claimAllRadStakeRewards(this.accountAddress);
          Notify({ type: "success", message: "质押收益提取成功" });
        } else if (type === "promotion") {
          await claimRadShareReward();
          Notify({ type: "success", message: "推广收益提取成功" });
        } else if (type === "card") {
          await claimRadCardsReward();
          Notify({ type: "success", message: "卡牌收益提取成功" });
        }
        await this.loadPageData();
      } catch (err) {
        Notify({ type: "danger", message: err?.message || "提取失败" });
      } finally {
        this.withdrawingType = "";
      }
    },
    async onActivate() {
      if (!this.accountAddress) {
        Notify({ type: "warning", message: "请先连接钱包" });
        return;
      }

      this.activating = true;
      try {
        await activateAllCards();
        Notify({ type: "success", message: "激活成功" });
        await this.loadPageData();
        await this.$refs.userInfoCard?.loadCardProfile();
      } catch (err) {
        if (err?.message && !err?.isCancelled) {
          Notify({ type: "warning", message: err.message });
        }
      } finally {
        this.activating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.rad-stake-page {
  min-height: 100%;
  padding: 16px 0 20px;
}
</style>
